import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatAbsolute } from '../utils/time';
import ShareBadge from './ShareBadge';
import OnboardingChecklist from './OnboardingChecklist';
import { getListColorHeaderBg, getListColorTextValue, LIST_COLORS } from '../lib/listColors';
import { createDragAutoScroll } from '../lib/dragAutoScroll';
import PortalMenu from './PortalMenu';
import { MenuItem, SubMenu, ColorSwatches, IconPencil, IconColor, IconTrash, IconShare, IconSort } from './MenuKit';

// Sidebar listing all notes on this board.
// Two sections: Main (open + closed interleaved in user order) and Archive.
// Rows show title + first line + optional state pill.
// Drag-and-drop reorders within a section (closing/archiving a note also lands it
// back in its same section's order slot - state is decoupled from order).
//
// Row click behaviour:
//   open     -> focus the panel on the canvas
//   closed   -> reopen (state='open') + focus
//   archived -> open the panel on the canvas WITHOUT changing state (shows
//               an "archived" banner with an unarchive button inside the panel)
// Hover row actions provide explicit archive / unarchive / delete without opening.

const MIN_W = 180;
const MAX_W = 480;

// Sort helper: order field asc, with null orders at the END (default
// "unplaced" group). Walter 2026-05-13: tiebreaker for the null group
// is createdAt ascending — newer lists sit lower, so a newly-created
// list naturally appears at the bottom of the sidebar even when other
// null-order lists exist above. id is the final tiebreaker for legacy
// docs that pre-date createdAt.
function sortByOrder(a, b) {
  const ao = a.order == null ? Number.POSITIVE_INFINITY : a.order;
  const bo = b.order == null ? Number.POSITIVE_INFINITY : b.order;
  if (ao !== bo) return ao - bo;
  const at = a.createdAt?.toMillis?.()
    ?? (a.createdAt ? new Date(a.createdAt).getTime() : 0);
  const bt = b.createdAt?.toMillis?.()
    ?? (b.createdAt ? new Date(b.createdAt).getTime() : 0);
  if (at !== bt) return at - bt;
  return (a.id || '').localeCompare(b.id || '');
}

// First non-empty line of content, trimmed. Used as the row subtitle.
// Leading 🔔 is stripped because designated reminder lists already
// have a 🔔 marker on their title row — a second bell on the
// preview line was visual clutter (Walter's nav-busyness call).
function firstLine(content) {
  if (!content) return '';
  for (const line of content.split('\n')) {
    let t = line.trim();
    if (!t) continue;
    // Strip a leading bell (with optional whitespace after) so a
    // reminder line like "🔔 may 8 8am" previews as just "may 8 8am".
    t = t.replace(/^\u{1F514}\s*/u, '');
    if (t) return t;
  }
  return '';
}

export default function Sidebar({
  lists,
  focusedId,
  width,
  // Active board's colour washes the left nav so it matches the top nav bar's
  // wash (Walter 2026-07-03). null = no wash.
  boardColor,
  onResize,
  onSelectRow,
  onRowDoubleClick,
  onArchive,
  onUnarchive,
  onDelete,
  onReorder,
  // Walter 2026-05-16: tap-to-sort affordance in the "main" section
  // header. Receives a mode string: 'canvas' (match the column /
  // free-layout arrangement on the board) or 'alpha' (A → Z by
  // title). Parent rewrites each list's `order` to match the chosen
  // sort — Tab cycling + drag-reorder ordering both follow the
  // sidebar order, so this single feature also fixes Tab's habit of
  // cycling in a different sequence than the canvas eye-scan.
  onSortLists,
  // Compact mode for the mobile small-nav strip: fixed 80px wide, smaller
  // rows, no hover actions, no resize handle. Taps are still reported back
  // through onSelectRow — the mobile parent decides what to do with them.
  compact = false,
  // Touch mode: when true AND not compact, row action buttons collapse into a
  // single `⋯` fold-out menu (move → / archive / delete). Mobile has no hover
  // state so the old hover-on-row icon strip was useless there.
  touch = false,
  // Move-to-board support inside the touch-mode ⋯ menu. When both are present
  // and `boards` has at least one other board, the menu gets a `move to →`
  // submenu. Without them the menu just shows archive + delete.
  boards = [],
  currentBoardId,
  onMoveList,
  // Array of list IDs that are designated as reminder surfaces. Any
  // row whose list ID is in this array gets a 🔔 marker before its
  // title. Plural so a user can have multiple designated lists
  // (work + personal, etc.).
  designatedReminderListIds = [],
  // Toggle whether a given list is in the designated set. Hooked up
  // when the user is in the reminders feature; if absent, the menu
  // option won't render. Receives the list ID.
  onToggleReminderList,
  // Whether the reminders feature is on for this user — gates
  // visibility of the "designate as reminder list" menu item so we
  // don't surface it when the feature is off.
  remindersFeatureOn = false,
  // Open share modal for a specific list (Phase 2 sharing). Null
  // when sharing alpha is off — menu item then doesn't render.
  onShareList,
  // Peek the share state on a list (always wired, alpha-independent).
  // Backs the ShareBadge click on each row. The badge itself only
  // renders when the list has share state, so wiring this for
  // non-alpha users is harmless (no badge → no click target). For
  // alpha users it duplicates onShareList as a more discoverable
  // affordance: the menu item still works for inviting.
  onPeekShare,
  // Pointer docs for boards/lists shared with the current user — see
  // useSharedWithMe. Rendered as a section at the bottom of the
  // sidebar so the user doesn't need to bookmark share URLs to find
  // their way back. Empty array hides the section entirely.
  sharedWithMe = [],
  // Walter 2026-05-11: per-user shared-list placements. Used here to
  // filter the "shared with me" section: once an invitee has placed
  // a shared list inside one of their own boards, it stops appearing
  // in this section and instead renders inline with that board's
  // lists (BoardPage's merge layer handles the rendering). Shared
  // BOARDS aren't affected — only list-kind shares move out of the
  // sidebar section when placed.
  sharedListPlacements = {},
  // Navigate to a board by id. Reused from the existing board switcher;
  // sharedWithMe entries call this when clicked.
  onNavigateBoard,
  // Navigate to a standalone list page (per-list shares). Used by
  // SharedWithMeSection when the user clicks a shared LIST entry.
  onNavigateList,
  // Open the New Board modal. Wired from BoardPage's existing
  // handleNewBoard handler so the boards-section "+ new board" row
  // shares the path the old top-dropdown entry used.
  onNewBoard,
  // Open the /trash page. When provided, a trash button is pinned at
  // the bottom of the sidebar (desktop only — compact mobile strip
  // suppresses it). When absent (e.g. mobile compact path), no
  // footer renders. Mobile users reach trash via Settings modal →
  // Account section → trash row.
  onOpenTrash,
  // Live trash count for the footer badge. When 0, the footer is
  // rendered dim with no count; when >0, it gets an accent badge.
  // Per Walter's pushback: don't promote trash visually when empty.
  trashCount = 0,
  // Walter 2026-05-31: onboarding-checklist inputs. Rendered at the
  // top of the desktop sidebar (compact mobile skips it). isAnonymous
  // drives the "sign in" step; hasShared the "share a list" tip;
  // remindersCount (from `reminders` below) the "try a reminder" tip;
  // onRequestSignIn opens the account/sign-in surface.
  isAnonymous = false,
  hasShared = false,
  onRequestSignIn,
  // Walter 2026-06-06: opens Settings — drives the onboarding
  // "make it yours" step. Threaded down to OnboardingChecklist.
  onOpenSettings,
  // Walter 2026-06-13 (s2): onboarding "try a reminder list" step.
  onTryReminderList,
  hasReminderList = false,
  // Walter 2026-06-16: onboarding "keyboard shortcuts" step (desktop only —
  // BoardPage passes this; MobileBoard does not, so the step is absent there).
  onShowShortcuts,
  // Reminders array (passed through from BoardPage). Used to mark
  // boards in the sidebar that have at least one active reminder.
  reminders = [],
  // Map listId → boardId, for resolving reminders that don't carry
  // a sourceBoardId (older docs). Same prop the top-dropdown used.
  listIdToBoardId = {},
  // Board reorder via drag (Walter round 7): drag a board row,
  // drop on another board row to change order. (sourceId, targetId,
  // 'before' | 'after') — implementation lives in BoardPage's
  // useCallback that rewrites the `order` field on each board.
  onReorderBoards,
  // Walter 2026-06-26: per-board actions (rename / colour / delete) for the
  // sidebar board-row ⋯ menu. Same handlers the header board ⋯ uses.
  onRenameBoard,
  onUpdateBoard,
  onDeleteBoard,
  // Set a LIST's colour from the sidebar list ⋯ (colorId | null). Walter
  // 2026-06-29: parity with the board ⋯ + the canvas tile's colour.
  onSetListColor,
  // Preview-during-drag (Walter 2026-05-05): one-shot fetch of a
  // non-active board's lists so we can show its rows inline beneath
  // the board name during a list drag, letting the user drop at a
  // specific position in the target board. (targetBoardId) → Promise<Lists[]>.
  onFetchBoardLists,
}) {
  const navigate = useNavigate();
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [dropIndicator, setDropIndicator] = useState(null); // { id, pos: 'before' | 'after' }
  const resizeRef = useRef(null);
  const justDraggedRef = useRef(false); // suppresses the click that fires right after a drop

  const { main, archive } = useMemo(() => {
    const sorted = [...lists].sort(sortByOrder);
    const m = [];
    const a = [];
    for (const l of sorted) {
      if (l.state === 'archived') a.push(l);
      else m.push(l);
    }
    return { main: m, archive: a };
  }, [lists]);

  // Resize drag
  const onResizeDown = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = width;
    const onMove = (ev) => {
      const nw = Math.max(MIN_W, Math.min(MAX_W, startW + (ev.clientX - startX)));
      onResize(nw);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [width, onResize]);

  // Drag-and-drop reorder within a section.
  // During dragover we decide "before or after" based on whether the cursor is
  // in the top or bottom half of the target row, and show a thin indicator line.
  const handleDragStart = (e, id) => {
    setDragId(id);
    justDraggedRef.current = true;
    // Firefox requires dataTransfer.setData to actually start a drag; Chrome is
    // lenient but we set it for consistency. effectAllowed unifies the cursor icon.
    //
    // The custom MIME type lets BoardsSection rows recognize a list
    // drag specifically (vs any other drag source) without having to
    // peek at React state — the dataTransfer is the only thing that
    // crosses the drag boundary cleanly. The plain-text payload is
    // kept for the existing reorder code path.
    try {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(id));
      e.dataTransfer.setData('application/x-listicity-list-id', String(id));
    } catch {}
  };
  const handleDragEnd = () => {
    setDragId(null);
    setDropIndicator(null);
    // clear the "suppress click" flag a tick later, after the click event has fired.
    setTimeout(() => { justDraggedRef.current = false; }, 0);
  };

  const handleDragOver = (e, overId, section) => {
    if (dragId == null || dragId === overId) return;
    // Only allow reorder within the same section as the dragged row.
    const inSame = section.some(l => l.id === dragId) && section.some(l => l.id === overId);
    if (!inSame) return;
    e.preventDefault();
    try { e.dataTransfer.dropEffect = 'move'; } catch {}
    const rect = e.currentTarget.getBoundingClientRect();
    const mid = rect.top + rect.height / 2;
    const pos = e.clientY < mid ? 'before' : 'after';
    if (!dropIndicator || dropIndicator.id !== overId || dropIndicator.pos !== pos) {
      setDropIndicator({ id: overId, pos });
    }
  };

  // Shared reorder-commit: move `draggedId` to `pos` ('before'|'after') `overId`
  // within `section`. Used by BOTH the HTML5 drop (desktop) and the long-press
  // drag (touch, below). Walter 2026-07-03.
  const applyReorder = (draggedId, overId, pos, section) => {
    if (draggedId == null || overId == null || draggedId === overId) return;
    const sorted = [...section].sort(sortByOrder);
    const draggedItem = sorted.find(l => l.id === draggedId);
    if (!draggedItem) return;
    const reduced = sorted.filter(l => l.id !== draggedId);
    let insertAt = reduced.findIndex(l => l.id === overId);
    if (insertAt < 0) return;
    if (pos === 'after') insertAt += 1;
    // Compute the final order of items after the move.
    const finalOrder = [...reduced.slice(0, insertAt), draggedItem, ...reduced.slice(insertAt)];
    // If ANY item lacks an order field (legacy data), renumber the whole section
    // sequentially — neighbor-averaging produces junk when neighbors are
    // undefined. Spacing of 10 leaves room for later single inserts.
    const anyMissing = finalOrder.some(it => it.order == null);
    if (anyMissing) {
      finalOrder.forEach((it, idx) => {
        const desired = (idx + 1) * 10;
        if (it.order !== desired) onReorder(it.id, desired);
      });
    } else {
      // Fast path: a single write moves the dragged item between its neighbours.
      const newIdx = finalOrder.findIndex(it => it.id === draggedId);
      const prev = finalOrder[newIdx - 1];
      const next = finalOrder[newIdx + 1];
      const prevOrder = prev?.order;
      const nextOrder = next?.order;
      let newOrder;
      if (prevOrder != null && nextOrder != null) newOrder = (prevOrder + nextOrder) / 2;
      else if (prevOrder != null) newOrder = prevOrder + 1;
      else if (nextOrder != null) newOrder = nextOrder - 1;
      else newOrder = 0;
      onReorder(draggedId, newOrder);
    }
  };

  const handleDrop = (e, overId, section) => {
    e.preventDefault();
    e.stopPropagation();
    const pos = dropIndicator?.pos === 'after' ? 'after' : 'before';
    applyReorder(dragId, overId, pos, section);
    handleDragEnd();
  };

  // Long-press-drag reorder for TOUCH (Walter 2026-07-03). Desktop uses HTML5
  // DnD; touch has no native DnD, so: press-and-hold a row (~400ms) to pick it
  // up, drag to move an insertion line, release to drop. A short tap (released
  // before the hold fires) just selects the row. Moving the finger before the
  // hold cancels — that's a scroll, not a drag.
  const touchDragRef = useRef(null);
  // The sidebar's scrollable section — target for drag edge auto-scroll
  // (Walter 2026-07-04, mobile long-press reorder).
  const scrollAreaRef = useRef(null);
  const startTouchDrag = (e, listId, section) => {
    if (!e.touches || e.touches.length !== 1) return;
    const t0 = e.touches[0];
    const st = { listId, section, startX: t0.clientX, startY: t0.clientY, active: false, overId: null, pos: 'before', timer: null };
    touchDragRef.current = st;

    // Walter 2026-07-04: while holding, we preventDefault to stop the page
    // scrolling — so the ONLY way to reach a row past the fold is programmatic
    // auto-scroll. Feed the finger position to the shared helper; on each
    // scroll step re-run the hit-test so the insertion line tracks the row now
    // under the finger.
    let lastX = t0.clientX;
    let lastY = t0.clientY;
    const hitTest = (cx, cy) => {
      const el = document.elementFromPoint(cx, cy);
      const rowEl = el && el.closest ? el.closest('a[href^="/list/"]') : null;
      if (!rowEl) { st.overId = null; setDropIndicator(null); return; }
      const overId = rowEl.getAttribute('href').replace('/list/', '');
      if (overId === st.listId || !st.section.some(l => l.id === overId)) { st.overId = null; setDropIndicator(null); return; }
      const rect = rowEl.getBoundingClientRect();
      const pos = cy < rect.top + rect.height / 2 ? 'before' : 'after';
      st.overId = overId; st.pos = pos;
      setDropIndicator({ id: overId, pos });
    };
    const scroller = createDragAutoScroll({
      getContainer: () => scrollAreaRef.current,
      onScroll: () => hitTest(lastX, lastY),
    });

    const onMove = (ev) => {
      const touch = ev.touches && ev.touches[0];
      if (!touch) return;
      if (!st.active) {
        // Pre-hold: any real movement means the user is scrolling, not holding.
        if (Math.abs(touch.clientX - st.startX) > 8 || Math.abs(touch.clientY - st.startY) > 8) cleanup();
        return;
      }
      ev.preventDefault(); // holding — block the page from scrolling under us
      lastX = touch.clientX;
      lastY = touch.clientY;
      scroller.update(touch.clientX, touch.clientY);
      hitTest(touch.clientX, touch.clientY);
    };
    const onEnd = () => {
      const wasActive = st.active;
      const { overId, pos, section: sec } = st;
      cleanup();
      if (wasActive) {
        if (overId) applyReorder(listId, overId, pos, sec);
        justDraggedRef.current = true; // swallow the click that follows touchend
        setTimeout(() => { justDraggedRef.current = false; }, 60);
      }
    };
    function cleanup() {
      if (st.timer) { clearTimeout(st.timer); st.timer = null; }
      scroller.stop();
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onEnd);
      if (st.active) { setDragId(null); setDropIndicator(null); }
      if (touchDragRef.current === st) touchDragRef.current = null;
    }

    st.timer = setTimeout(() => {
      if (touchDragRef.current !== st) return;
      st.active = true;
      setDragId(listId); // reuse the drag visual (dimmed row)
      try { if (navigator.vibrate) navigator.vibrate(15); } catch { /* no haptics */ }
    }, 400);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
    document.addEventListener('touchcancel', onEnd);
  };

  const effectiveWidth = compact ? 80 : width;

  // Lists+archive renderer. Extracted so we can call it (a) inline
  // under the active board in BoardsSection on desktop, and (b) at
  // the sidebar root in compact mode where there's no boards section.
  const renderListsAndArchive = () => (
    <>
      {/* Walter 2026-06-02: dropped the "MAIN" section header + its
          collapse chevron — you never collapse your own active lists,
          and the label confused a test user. Lists now render directly
          under the board. The sort affordance moves to a minimal
          right-aligned control where the header used to be. (ARCHIVE
          below keeps its header — collapsing archived lists is the one
          genuinely useful collapse.) */}
      {/* Walter 2026-06-02: the list-sort affordance moved UP next to the
          board name (see BoardsSection) — "it logically belongs next to
          the board." Just a small spacer here so the first list isn't
          flush against the board row. */}
      <div style={{ height: compact ? 4 : 6 }} />
      {main.length === 0 && (
        <EmptyRow text="no notes yet" compact={compact} />
      )}
      {main.map((l, idx) => (
        <Row
          key={l.id}
          list={l}
          section="main"
          isFocused={focusedId === l.id}
          isDragging={dragId === l.id}
          dropIndicator={dropIndicator?.id === l.id ? dropIndicator.pos : null}
          onClick={() => { if (justDraggedRef.current) return; onSelectRow(l); }}
          onArchive={() => onArchive(l)}
          onUnarchive={null}
          onDelete={() => onDelete(l)}
          onDragStart={(e) => handleDragStart(e, l.id)}
          onDragEnd={handleDragEnd}
          onDragOver={(e) => handleDragOver(e, l.id, main)}
          onDrop={(e) => handleDrop(e, l.id, main)}
          onDoubleClick={onRowDoubleClick ? () => onRowDoubleClick(l) : null}
          compact={compact}
          touch={touch}
          // Long-press-drag reorder on touch (Walter 2026-07-03).
          onTouchStart={touch ? (e) => startTouchDrag(e, l.id, main) : undefined}
          boards={boards}
          currentBoardId={currentBoardId}
          onMoveList={onMoveList}
          isDesignatedReminderList={Array.isArray(designatedReminderListIds) && designatedReminderListIds.includes(l.id)}
          onToggleReminderList={remindersFeatureOn && onToggleReminderList ? () => onToggleReminderList(l.id) : null}
          onSetColor={onSetListColor ? (colorId) => onSetListColor(l.id, colorId) : null}
          onShareList={onShareList ? () => onShareList(l) : null}
          onPeekShare={onPeekShare ? () => onPeekShare(l) : null}
          // Desktop ⋯ menu "↑ move up" / "↓ move down" entries (kept for the
          // hover menu; mobile reorder is the long-press drag above). Null at
          // the boundaries so the menu hides those entries.
          onMoveUp={onReorder && idx > 0
            ? () => { onReorder(l.id, idx - 1); onReorder(main[idx - 1].id, idx); }
            : null}
          onMoveDown={onReorder && idx < main.length - 1
            ? () => { onReorder(l.id, idx + 1); onReorder(main[idx + 1].id, idx); }
            : null}
        />
      ))}

      <SectionHeader
        label="archive"
        count={archive.length}
        open={archiveOpen}
        onToggle={() => setArchiveOpen(v => !v)}
        subtle
        compact={compact}
      />
      {archiveOpen && archive.length === 0 && (
        <EmptyRow text="nothing archived" compact={compact} />
      )}
      {archiveOpen && archive.map((l, idx) => (
        <Row
          key={l.id}
          list={l}
          section="archive"
          isFocused={focusedId === l.id}
          isDragging={dragId === l.id}
          dropIndicator={dropIndicator?.id === l.id ? dropIndicator.pos : null}
          onClick={() => { if (justDraggedRef.current) return; onSelectRow(l); }}
          onArchive={null}
          onUnarchive={() => onUnarchive(l)}
          onDelete={() => onDelete(l)}
          onDragStart={(e) => handleDragStart(e, l.id)}
          onDragEnd={handleDragEnd}
          onDragOver={(e) => handleDragOver(e, l.id, archive)}
          onDrop={(e) => handleDrop(e, l.id, archive)}
          onDoubleClick={onRowDoubleClick ? () => onRowDoubleClick(l) : null}
          compact={compact}
          touch={touch}
          onTouchStart={touch ? (e) => startTouchDrag(e, l.id, archive) : undefined}
          onMoveUp={onReorder && idx > 0
            ? () => { onReorder(l.id, idx - 1); onReorder(archive[idx - 1].id, idx); }
            : null}
          onMoveDown={onReorder && idx < archive.length - 1
            ? () => { onReorder(l.id, idx + 1); onReorder(archive[idx + 1].id, idx); }
            : null}
          boards={boards}
          currentBoardId={currentBoardId}
          onMoveList={onMoveList}
          isDesignatedReminderList={Array.isArray(designatedReminderListIds) && designatedReminderListIds.includes(l.id)}
          onToggleReminderList={remindersFeatureOn && onToggleReminderList ? () => onToggleReminderList(l.id) : null}
          onSetColor={onSetListColor ? (colorId) => onSetListColor(l.id, colorId) : null}
          onShareList={onShareList ? () => onShareList(l) : null}
          onPeekShare={onPeekShare ? () => onPeekShare(l) : null}
        />
      ))}
    </>
  );

  return (
    <div style={{
      width: effectiveWidth + 'px',
      flexShrink: 0,
      // Walter 2026-07-03: the left nav washes with the active board's colour to
      // match the top nav bar (both use --surface base at 0.16).
      background: getListColorHeaderBg(boardColor, 'var(--surface)', 0.16) || 'var(--surface)',
      borderRight: '1px solid var(--border)',
      display: 'flex', flexDirection: 'column',
      position: 'relative',
      overflow: 'hidden',
      transition: 'width 0.2s ease',
    }}>
      {/* Sections */}
      <div ref={scrollAreaRef} style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {/* Desktop / peek-expanded: full structured nav with BOARDS
            section (active board expanded inline showing lists/archive)
            + SHARED WITH ME.
            Compact (80px peek): a slim boards mini-list at top so
            the user can switch boards in 1 tap without expanding —
            Walter's II.5. Below it, the active board's lists.
            SHARED WITH ME isn't surfaced in compact (no room with
            useful labels at 80px); use peek-expanded for that. */}
        {compact ? (
          <CompactBoardsMini
            boards={boards}
            currentBoardId={currentBoardId}
            onNavigateBoard={onNavigateBoard}
            activeListsContent={renderListsAndArchive()}
          />
        ) : (
          <>
            <OnboardingChecklist
              isAnonymous={isAnonymous}
              remindersCount={Array.isArray(reminders) ? reminders.length : 0}
              hasShared={hasShared}
              onRequestSignIn={onRequestSignIn}
              onOpenSettings={onOpenSettings}
              onTryReminderList={onTryReminderList}
              hasReminderList={hasReminderList}
              onShowShortcuts={onShowShortcuts}
            />
            <BoardsSection
              boards={boards}
              currentBoardId={currentBoardId}
              onNavigateBoard={onNavigateBoard}
              onNewBoard={onNewBoard}
              activeListsContent={renderListsAndArchive()}
              onSortLists={onSortLists}
              canSortActive={!!onSortLists && !compact && main.length >= 2}
              reminders={reminders}
              listIdToBoardId={listIdToBoardId}
              onPeekShareBoard={(b) => onPeekShare?.(b)}
              onMoveList={onMoveList}
              onFetchBoardLists={onFetchBoardLists}
              onReorderBoards={onReorderBoards}
              onRenameBoard={onRenameBoard}
              onUpdateBoard={onUpdateBoard}
              onDeleteBoard={onDeleteBoard}
            />
            <SharedWithMeSection
              items={Array.isArray(sharedWithMe) ? sharedWithMe : []}
              placements={sharedListPlacements || {}}
              currentBoardId={currentBoardId}
              onNavigateBoard={onNavigateBoard}
              onNavigateList={onNavigateList}
            />
          </>
        )}
      </div>

      {/* Trash footer — pinned at the bottom of the sidebar so it's
          one click away during normal canvas work, instead of buried
          two levels deep in the account popover. Compact (mobile strip)
          mode suppresses it: no room for the label, and mobile users
          reach trash via Settings → Account → trash. Count badge is
          shown only when >0 (per Walter: don't promote trash visually
          when empty). */}
      {/* Sidebar footer — trash + marketing/legal links. Compact (80px peek)
          suppresses it — no room. Walter 2026-07-03: DESKTOP is the original
          layout (trash on its own line, then the 6 links in a left-aligned
          3-column grid); the brief single-row version cut off in the narrow
          desktop sidebar. TOUCH keeps a single swipeable line (scrollbar hidden)
          since the phone footer spans the full viewport width. */}
      {!compact && (
        <div style={{
          flexShrink: 0,
          borderTop: '1px solid var(--border)',
          padding: '6px 14px',
        }}>
          {(() => {
            // Links shared by both layouts. Routes via the SPA router — plain
            // hrefs were dead on prod; href kept for middle-click / a11y.
            const linkStyle = touch
              ? { ...sidebarFooterLinkStyle, flexShrink: 0, whiteSpace: 'nowrap' }
              : sidebarFooterLinkStyle;
            const links = [
              ['/about', 'about'], ['/help', 'help'], ['/privacy', 'privacy'],
              ['/terms', 'terms'], ['/contact', 'contact'],
            ].map(([href, label]) => (
              <a
                key={href}
                href={href}
                style={linkStyle}
                aria-label={label}
                title={label}
                onClick={(e) => {
                  if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return; // let new-tab through
                  e.preventDefault();
                  navigate(href);
                }}
              >
                {label}
              </a>
            ));
            if (onShowShortcuts) {
              links.push(
                <button
                  key="shortcuts"
                  type="button"
                  onClick={onShowShortcuts}
                  style={{ ...linkStyle, background: 'none', border: 'none', cursor: 'pointer', font: 'inherit' }}
                  title="keyboard shortcuts"
                  onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-dim)'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-dimmer)'; }}
                >
                  shortcuts
                </button>,
              );
            }

            // Phone: trash + links on ONE swipeable line, scrollbar hidden.
            if (touch) {
              return (
                <nav className="sidebar-footer-nav" style={{
                  display: 'flex', alignItems: 'center', flexWrap: 'nowrap',
                  gap: '14px', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none',
                  fontFamily: 'var(--mono)', fontSize: 'calc(11px * var(--ui-scale, 1))',
                  color: 'var(--text-dimmer)', letterSpacing: '0.3px',
                }}>
                  {onOpenTrash && <TrashFooter onClick={onOpenTrash} count={trashCount} />}
                  {links}
                </nav>
              );
            }
            // Desktop: trash on its OWN line above a left-aligned 3-column grid.
            return (
              <>
                {onOpenTrash && (
                  <div style={{ marginBottom: 8 }}>
                    <TrashFooter onClick={onOpenTrash} count={trashCount} />
                  </div>
                )}
                <nav style={{
                  display: 'grid', gridTemplateColumns: 'repeat(3, auto)',
                  justifyContent: 'start', justifyItems: 'start', gap: '5px 18px',
                  fontFamily: 'var(--mono)', fontSize: 'calc(11px * var(--ui-scale, 1))',
                  color: 'var(--text-dimmer)', letterSpacing: '0.3px',
                }}>
                  {links}
                </nav>
              </>
            );
          })()}
        </div>
      )}

      {/* Resize handle on right edge — desktop only. */}
      {!compact && (
        <div
          ref={resizeRef}
          onMouseDown={onResizeDown}
          style={{
            position: 'absolute', top: 0, right: 0, bottom: 0, width: '4px',
            cursor: 'col-resize',
            background: 'transparent',
            zIndex: 2,
          }}
          // Walter 2026-05-05 (slide 2): the orange hover read as a
          // stray accent line in narrow-desktop view. Switched to the
          // bright-border shade so the resize affordance still pops
          // on hover but doesn't compete with brand accents.
          onMouseEnter={e => e.currentTarget.style.background = 'var(--border-bright)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        />
      )}
    </div>
  );
}

// Inline footer-link styling shared by all four marketing-page links.
// Walter 2026-05-25 (Bug #9 — round-2 tester report): bare text links
// had no padding so the tap zone was just the glyph bounding box,
// making them very hard to hit on mobile (and they sat close together
// at the bottom edge of the screen). The padding here expands the hit
// area without much visible layout change.
const sidebarFooterLinkStyle = {
  color: 'var(--text-dimmer)',
  textDecoration: 'none',
  borderBottom: '1px dotted transparent',
  transition: 'color 0.12s, border-color 0.12s',
  padding: '8px 4px',
  margin: '-4px 0',
  display: 'inline-block',
};

// Section header
function SectionHeader({ label, count, open, onToggle, subtle, compact, rightAddon }) {
  return (
    <div
      onClick={onToggle}
      style={{
        display: 'flex', alignItems: 'center', gap: compact ? '4px' : '6px',
        padding: compact ? '8px 8px 4px' : '10px 12px 6px',
        fontFamily: 'var(--mono)',
        fontSize: compact
          ? 'calc(9px * var(--ui-scale, 1))'
          : 'calc(10px * var(--ui-scale, 1))',
        fontWeight: 600,
        letterSpacing: compact ? '0.5px' : '0.8px', textTransform: 'uppercase',
        color: subtle ? 'var(--text-dimmer)' : 'var(--text-dim)',
        cursor: 'pointer',
        userSelect: 'none',
        borderTop: subtle ? '1px solid var(--border)' : 'none',
        marginTop: subtle ? '8px' : 0,
      }}
    >
      <svg
        width="8" height="8" viewBox="0 0 8 8" fill="none"
        style={{
          transition: 'transform 0.15s',
          transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
          flexShrink: 0,
        }}
      >
        <path d="M2 1l3 3-3 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      <span style={{ opacity: 0.55, fontWeight: 400 }}>{count}</span>
      {rightAddon ? (
        <>
          <div style={{ flex: 1 }} />
          {rightAddon}
        </>
      ) : null}
    </div>
  );
}

function EmptyRow({ text, compact }) {
  return (
    <div style={{
      padding: compact ? '8px 10px' : '10px 14px',
      fontFamily: 'var(--mono)',
      fontSize: compact
        ? 'calc(10px * var(--ui-scale, 1))'
        : 'calc(11px * var(--ui-scale, 1))',
      color: 'var(--text-dimmer)', fontStyle: 'italic',
      whiteSpace: compact ? 'nowrap' : 'normal',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }}>
      {text}
    </div>
  );
}

// Row
function Row({
  list, section, isFocused, isDragging, dropIndicator,
  onClick, onDoubleClick, onArchive, onUnarchive, onDelete,
  onDragStart, onDragEnd, onDragOver, onDrop,
  compact, touch, isDesignatedReminderList,
  boards, currentBoardId, onMoveList,
  // Optional: when provided, the ⋯ menu shows a "designate as
  // reminder list" / "remove as reminder list" toggle. Receives no
  // args; caller knows which list this Row is rendering.
  onToggleReminderList,
  // Optional: open Share modal for this list. Receives no args.
  onShareList,
  // Optional: set this list's colour (colorId | null). Backs the colour
  // swatch row in the ⋯ menu. Walter 2026-06-29.
  onSetColor,
  // Optional: peek share state on this list (always wired, alpha-
  // independent). Backs the ShareBadge click — used by both owner
  // and guest to see who else has access.
  onPeekShare,
  // Walter 2026-05-15: tap-to-step reorder for touch devices where
  // drag-and-drop is unreliable (long-press triggers OS context menu
  // instead of starting a drag). When provided, the touch ⋯ menu
  // surfaces "↑ move up" / "↓ move down" entries. Null at the
  // boundaries (top/bottom of section).
  onMoveUp,
  onMoveDown,
  // Touch long-press-drag reorder — starts the press-and-hold gesture.
  onTouchStart,
}) {
  const [hover, setHover] = useState(false);
  // ⋯ menu (desktop hover + touch). Portalled, so colour + move-to can fly out
  // to the side without the sidebar's overflow:hidden clipping them.
  const [menuOpen, setMenuOpen] = useState(false);
  const kebabRef = useRef(null);
  let title = list.title?.trim() || '';
  // Designated reminder lists: their titles often begin with a "🔔 "
  // prefix (the system used to render bells inline). Now the bell is
  // a separate dim glyph at the right edge of the row, so a leading
  // bell in the title is redundant + double-renders. Strip it.
  if (isDesignatedReminderList) {
    title = title.replace(/^\u{1F514}\s*/u, '').trim();
  }
  const first = firstLine(list.content);
  // Always show "(empty)" as the header when a tile has no title, even when
  // the content has a first line. Previously we promoted the first content
  // line into the header slot — that was confusing when users had set an
  // intentionally blank title but wanted to spot those tiles in the list.
  // The first line still appears below as a subtitle preview.
  const displayTitle = title || '(empty)';
  const displaySub = first;
  const isClosed = list.state === 'closed';
  // Desktop hover strip vs touch ⋯ menu: same gating (row must be non-compact
  // and either hovered or in touch mode), two different renderings below.
  const showActions = !compact && (hover || touch);
  // Walter 2026-06-29: a coloured list shows a subtle colour BAR down the left
  // edge (the row-background wash drew too much attention). The focus indicator
  // (accent bar) still wins when the row is the Tab target — focus is transient
  // and the colour bar returns the moment focus moves on.
  // Walter 2026-06-29 (rethink): the list COLOUR washes the row BACKGROUND (like
  // the tile), and the ACTIVE/focused list is marked by a left ACCENT BAR — not
  // a background change. "More intuitive."
  const leftBorderW = isFocused ? 3 : 0;
  const leftBarColor = isFocused ? 'var(--accent)' : 'transparent';

  const otherBoards = useMemo(
    () => (boards || []).filter(b => b.id !== currentBoardId),
    [boards, currentBoardId],
  );
  const hasMove = !!onMoveList && otherBoards.length > 0;
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  // Desktop HTML5 drag fades the source row (a native ghost follows the cursor).
  // Touch long-press-drag has no ghost — the row IS the picked-up element, so it
  // POPS forward (elevated + accent ring) instead of fading. Walter 2026-07-03.
  const touchDragging = isDragging && touch;

  return (
    <a
      // Walter's C (round 5): real anchor href so middle-click /
      // ctrl/cmd-click / right-click "Open in new tab" all work
      // natively on sidebar list rows. Plain click prevents default
      // and falls through to the existing onSelectRow handler so the
      // SPA focus-on-canvas behavior is unchanged. The /list/:id
      // route resolves own lists too via useResolvedList — same path
      // shared lists already use.
      href={`/list/${list.id}`}
      title={list.updatedAt ? `last edited ${formatAbsolute(list.updatedAt)}` : undefined}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onTouchStart={onTouchStart}
      onClick={(e) => {
        if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return;
        // Always stop the native anchor navigation for a plain tap/click.
        e.preventDefault();
        // Walter 2026-07-03: on touch, a tap on the ⋯ button sometimes falls
        // through to this row-level handler (stopPropagation on the button is
        // unreliable on mobile), which then navigated + collapsed the nav so
        // the ⋯ menu never opened. Explicitly ignore taps inside the ⋯ area so
        // the row never selects/navigates when the user meant to open the menu.
        if (e.target.closest && e.target.closest('[data-row-menu]')) return;
        onClick?.(e);
      }}
      onDoubleClick={onDoubleClick || undefined}
      // Walter 2026-07-03: on touch, long-press was popping the OS link menu
      // ("open in new tab / copy link") — useless here and blocks any future
      // long-press gesture. Suppress it on touch; desktop keeps right-click.
      onContextMenu={touch ? (e) => e.preventDefault() : undefined}
      // No hover tracking on touch — a tap (and especially a long-press drag)
      // fires a synthetic mouseenter with no matching mouseleave, leaving the
      // row stuck in its hover background until something else re-renders it
      // (Walter 2026-07-03).
      onMouseEnter={touch ? undefined : () => setHover(true)}
      onMouseLeave={touch ? undefined : () => setHover(false)}
      style={{
        position: 'relative',
        textDecoration: 'none',
        display: 'block',
        WebkitTouchCallout: 'none',
        // Shift the row right by the focus-bar width so focused and
        // unfocused rows align vertically. Walter 2026-05-09: list
        // focus bar reduced from 3px to 2px so it reads as
        // hierarchically subordinate to the active-board's 3px bar.
        // Combined with the parent wrapper's marginLeft + left rail,
        // the visual chain is clear: active board (heavier bar) →
        // its lists (rail + indent) → focused list (lighter bar).
        // Left padding compensates for the left bar width so text stays aligned
        // whether the row is plain, focused, or coloured.
        padding: compact
          ? `5px 8px 5px ${8 - leftBorderW}px`
          : `7px 12px 7px ${12 - leftBorderW}px`,
        borderLeft: `${leftBorderW}px solid ${leftBarColor}`,
        // Picked-up (touch drag) → a full accent ring + opaque highlight so it
        // clearly reads as "activated / lifted", even where the box-shadow lift
        // below doesn't paint. Overrides borderLeft on all sides. Walter 2026-07-03.
        // 1px — 2px read as "a bit much" on device (Walter 2026-07-03).
        border: touchDragging ? '1px solid var(--accent)' : undefined,
        // Walter 2026-07-03: active/focused list = accent BAR + accent-wash
        // BACKGROUND (both). The list's own COLOUR is no longer shown via the
        // background — it now rides the name's font colour (below). Hover wins
        // over focus so you get feedback on the row you're about to click.
        // NB: the wash is a doubled linear-gradient (not a solid colour) — solid
        // background-color doesn't paint on these rows, but a gradient does (the
        // same reason getListColorHeaderBg uses gradients).
        background: touchDragging
          // Accent-tinted, OPAQUE highlight (accent wash over surface2) so the
          // lifted card stands out and doesn't show the list through it.
          ? 'linear-gradient(var(--accent-wash), var(--accent-wash)), linear-gradient(var(--surface2), var(--surface2))'
          : (hover
            ? 'linear-gradient(var(--surface2), var(--surface2))'
            : (isFocused ? 'linear-gradient(var(--accent-wash), var(--accent-wash))' : 'transparent')),
        cursor: touchDragging ? 'grabbing' : 'pointer',
        userSelect: 'none',
        // Desktop drag → fade the source; touch drag → lift it forward, full opacity.
        opacity: (isDragging && !touch) ? 0.4 : 1,
        transform: touchDragging ? 'scale(1.03)' : undefined,
        boxShadow: touchDragging ? '0 0 0 1px var(--accent), 0 10px 22px rgba(0,0,0,0.30)' : undefined,
        borderRadius: touchDragging ? 'var(--radius)' : undefined,
        zIndex: touchDragging ? 200 : undefined,
        transition: 'background 0.1s, padding 0.1s, transform 0.13s ease, box-shadow 0.13s ease',
      }}
    >
      {/* Drop indicators (above/below) */}
      {dropIndicator === 'before' && (
        <div style={{
          position: 'absolute', top: 0, left: 8, right: 8,
          height: '2px', background: 'var(--accent)', borderRadius: '1px',
          pointerEvents: 'none',
        }} />
      )}
      {dropIndicator === 'after' && (
        <div style={{
          position: 'absolute', bottom: 0, left: 8, right: 8,
          height: '2px', background: 'var(--accent)', borderRadius: '1px',
          pointerEvents: 'none',
        }} />
      )}

      {/* Row top: title + last-edited timestamp.
          - Timestamp is desktop-only (no room in compact strip) and hides
            when the hover strip is showing, so action icons never collide
            with the time label.
          - Right padding reserves space for the ⋯ menu (touch) or hover
            strip (desktop) so a long title truncates cleanly with an
            ellipsis instead of bleeding under the icons. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0,
        // Right-padding reserves space for the affordances that sit at
        // the right edge of the row:
        //   • desktop wide / non-compact: 76px for the always-shown
        //     hover-strip slot (Walter 2026-05-06 round 8 — kept
        //     reserved even when not hovering so badges don't jump).
        //   • touch wide / non-compact: 34px for the ⋯ menu button.
        //   • compact (80px mobile peek strip): no padding at all.
        //     The ⋯ button is suppressed in compact, the hover strip
        //     is gated on !compact, and there's no badges either.
        //     Reserving padding here just steals visible width from
        //     the title — Walter 2026-05-09 noticed list rows like
        //     "Wor…" / "Inb…" truncating to 3 chars on iPhone, while
        //     adjacent board rows used the same strip width better
        //     because they don't carry this padding.
        // Walter 2026-05-16: touch padding bumped 34 → 48. The ⋯
        // button is 32×32 anchored at right:8, so its left edge sits
        // at row_right - 40 — with the old 34px padding the text
        // ended just 6px clear of the icon and visually read as
        // "clashing." 48 gives a comfortable ~8px gap.
        // Touch has no persistent affordance (reorder is long-press-drag) — just
        // a small right margin; desktop reserves space for the hover strip.
        paddingRight: compact ? '0px' : (touch ? '12px' : '76px'),
      }}>
        {/* Drag affordance — only shown when the user is engaged with
            this row. Desktop: hover. Hidden in compact mode regardless.
            Walter 2026-05-15: hidden entirely on touch — the grip
            suggested "drag me to reorder" but drag-and-drop is broken
            on mobile (long-press fights the OS context menu), so the
            affordance was a lie. Touch reorder lives in the ⋯ menu as
            explicit "↑ move up" / "↓ move down" entries. */}
        {!compact && !touch && (hover || menuOpen) && <RowDragGrip />}
        {/* Walter 2026-05-14: title was flex:1 which made the span
            expand to fill the row even when the text was short.
            Text sat left-aligned, badges came AFTER the wide span,
            so users saw "Projects [...big gap...] [share]" with
            the icons floating mid-row instead of next to the title.
            Now flex: 0 1 auto — natural width, can shrink. Badges
            sit immediately after the text. The 76px right padding
            on the parent still reserves room for the hover strip. */}
        <span style={{
          flex: '0 1 auto',
          fontFamily: 'var(--mono)',
          fontSize: compact
            ? 'calc(11px * var(--ui-scale, 1))'
            : 'calc(12px * var(--ui-scale, 1))',
          // Walter 2026-07-03: a coloured list shows its COLOUR as the name's
          // font colour (bold, so it reads as intentional). The active/focused
          // list is marked by the bar + wash, so focus keeps accent text only
          // when the list has no colour of its own.
          fontWeight: (isFocused || list.color) ? 600 : 500,
          color: getListColorTextValue(list.color) || (isFocused ? 'var(--accent)' : 'var(--text)'),
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          minWidth: 0,
          transition: 'color 0.1s',
        }}>
          {displayTitle}
        </span>
        {/* Reminder + share markers — sit right after the title text
            now (was at the right edge of the row, which created a
            visible gap when the title was short). Greyed out so a
            wall of them in the sidebar doesn't shout. */}
        {!compact && isDesignatedReminderList && (
          <BellGlyph title="this is your designated reminder list — every line becomes a reminder, and lines right under it are its notes" />
        )}
        {!compact && (
          <ShareBadge
            editors={list.editors}
            viewers={list.viewers}
            shareToken={list.shareToken}
            onClick={() => onPeekShare?.()}
            tone="dim"
          />
        )}
        {/* Closed chip — surfaces "this list is closed (not on the
            canvas right now, double-click to reopen)" without forcing
            users to remember the state by row position. Compact mode
            (mobile small-nav strip) skips it for space. */}
        {isClosed && !compact && (
          <span
            title="closed (double-click to reopen)"
            style={{
              flexShrink: 0,
              fontFamily: 'var(--mono)',
              fontSize: 'calc(9px * var(--ui-scale, 1))',
              color: 'var(--text-dimmer)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius)',
              padding: '1px 5px',
              letterSpacing: '0.4px',
            }}
          >
            closed
          </span>
        )}
        {/* Walter 2026-05-06 round 7: dropped the always-visible
            relative-time chip ("3h", "9h", etc.) — it was adding
            noise to every row for information most users don't
            scan for. The full absolute date moved to the row's
            hover-tooltip via the `title` attribute on the row
            wrapper above. */}
      </div>

      {/* Row subtitle: first line — desktop only. The 80px compact strip has
          no room for a second line. Right-pad mirrors the title row so long
          subtitles also yield to the ⋯ menu / hover strip. */}
      {displaySub && !compact && (
        <div style={{
          marginTop: '2px',
          paddingRight: touch ? '48px' : '76px',
          fontFamily: 'var(--mono)',
          fontSize: 'calc(10px * var(--ui-scale, 1))',
          color: 'var(--text-dimmer)',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {displaySub}
        </div>
      )}

      {/* Walter 2026-06-26: the desktop hover-icon strip is gone — its
          archive/delete actions are now in the ⋯ menu below (which shows on
          desktop hover + touch), so there's ONE affordance everywhere. */}

      {/* Fold-out ⋯ menu — DESKTOP ONLY (hover). Walter 2026-07-03: removed on
          mobile/touch — the sidebar ⋯ was a persistent source of touch bugs
          (tap-through, menu-item taps collapsing the nav) and every action it
          offered is also reachable from the open tile's own ⋯ menu. `!touch`
          gates it out on mobile. */}
      {!touch && (showActions || menuOpen) && (
        <div
          // data-row-menu marks the ⋯ hit area so the row's onClick can ignore
          // taps here (see the <a> onClick above) — the reliable touch fix.
          data-row-menu
          // Walter 2026-06-29: vertically centre the ⋯ so it aligns on
          // single-line (empty) rows as well as two-line rows.
          style={{ position: 'absolute', top: '50%', right: 3, transform: 'translateY(-50%)', zIndex: menuOpen ? 1500 : 5 }}
        >
          <button
            ref={kebabRef}
            onClick={(e) => {
              // The row is an <a href="/list/:id"> — preventDefault so the click
              // doesn't navigate; stopPropagation so it doesn't select the row.
              e.preventDefault();
              e.stopPropagation();
              setMenuOpen(v => !v);
            }}
            title="more"
            style={{
              width: 32, height: 32,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              // Transparent so the ⋯ blends into the row instead of looking
              // like a stuck-on chip. The accent color flip on menuOpen is
              // enough to signal active state.
              background: 'transparent',
              border: 'none',
              borderRadius: 'var(--radius)',
              color: menuOpen ? 'var(--accent)' : 'var(--text-dim)',
              cursor: 'pointer', padding: 0,
              transition: 'color 0.15s',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
              <circle cx="6" cy="2.5" r="1" fill="currentColor"/>
              <circle cx="6" cy="6" r="1" fill="currentColor"/>
              <circle cx="6" cy="9.5" r="1" fill="currentColor"/>
            </svg>
          </button>
        </div>
      )}

      {/* ⋯ menu — portalled to document.body so it escapes the sidebar's
          overflow:hidden, and rendered with overflow:visible so the colour +
          move-to SubMenus can fly out to the side (on hover — tap on touch)
          instead of replacing the menu in place. Walter 2026-07-02. */}
      <PortalMenu
        open={menuOpen}
        triggerRef={kebabRef}
        onClose={closeMenu}
        minWidth={190}
        overflow="visible"
        zIndex={1600}
      >
        {/* ↑/↓ reorder — the tap-to-step alternative to drag on touch; hidden
            at the section boundaries (callbacks null). */}
        {onMoveUp && (
          <MenuItem onClick={() => { closeMenu(); onMoveUp(); }}>↑ move up</MenuItem>
        )}
        {onMoveDown && (
          <MenuItem onClick={() => { closeMenu(); onMoveDown(); }}>↓ move down</MenuItem>
        )}
        {hasMove && (
          <SubMenu label="move to" align="right">
            {otherBoards.map(b => (
              <MenuItem key={b.id} onClick={() => { closeMenu(); onMoveList(list.id, b.id); }}>
                {b.name}
              </MenuItem>
            ))}
          </SubMenu>
        )}
        {onUnarchive && (
          <MenuItem onClick={() => { closeMenu(); onUnarchive(); }}>unarchive</MenuItem>
        )}
        {onArchive && (
          <MenuItem onClick={() => { closeMenu(); onArchive(); }}>archive</MenuItem>
        )}
        {onToggleReminderList && (
          <MenuItem onClick={() => { closeMenu(); onToggleReminderList(); }}>
            {isDesignatedReminderList ? 'remove as reminder list' : 'designate as reminder list'}
          </MenuItem>
        )}
        {onShareList && (
          <MenuItem icon={<IconShare />} onClick={() => { closeMenu(); onShareList(); }}>share list</MenuItem>
        )}
        {/* Colour — folds out vertically to the side (named list matching the
            in-tile picker). */}
        {onSetColor && (
          <SubMenu icon={<IconColor />} label="colour" align="right">
            <MenuItem
              icon={<span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', border: '1px solid var(--border-bright)' }} />}
              onClick={() => { closeMenu(); onSetColor(null); }}
            >none</MenuItem>
            {LIST_COLORS.map(c => (
              <MenuItem
                key={c.id}
                icon={<span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: c.value }} />}
                onClick={() => { closeMenu(); onSetColor(c.id); }}
              >{c.label}</MenuItem>
            ))}
          </SubMenu>
        )}
        <MenuItem icon={<IconTrash />} color="var(--danger)" onClick={() => { closeMenu(); onDelete(); }}>delete</MenuItem>
      </PortalMenu>
    </a>
  );
}

/**
 * BoardsSection — top-of-sidebar block listing the user's own boards.
 *
 * The active board is rendered EXPANDED with the lists/archive sections
 * inline beneath it (passed in as `activeListsContent`). Other boards
 * render as collapsed name-only rows; click navigates to that board.
 *
 * Why collapsed-by-default for non-active boards: the "expand to see
 * lists without navigating" gesture is a bad UX in our case — users
 * almost always WANT to switch boards when they tap one, and the
 * extra "expand → click → navigate" is friction. So tap = nav.
 *
 * Reminder bell + share badge surface on collapsed rows so the
 * affordances aren't lost vs the old top-dropdown.
 */
function BoardsSection({
  boards,
  currentBoardId,
  onNavigateBoard,
  onNewBoard,
  activeListsContent,
  // Walter 2026-07-04: the list-sort control moved OFF the active board's
  // row (it read as clutter) INTO the board ⋮ menu. onSortLists('canvas'|
  // 'alpha') reorders the active board's lists; canSortActive gates the
  // menu item (only the active board, only when it has ≥2 lists).
  onSortLists,
  canSortActive,
  reminders,
  listIdToBoardId,
  onPeekShareBoard,
  // Cross-board list move (Walter's feedback 2026-05-05): drag a
  // sidebar list row from the active board, drop on a non-active
  // board row → move the list there. The drag source already sets
  // `application/x-listicity-list-id` on the dataTransfer; we read
  // it on drop. Reuses the existing useData.moveListToBoard pipe.
  onMoveList,
  // Board reorder (Walter round 7): drag a board row → drop on
  // another board row → renumber `order` fields. Distinguishes
  // from cross-board list drag via a different MIME type.
  onReorderBoards,
  onRenameBoard,
  onUpdateBoard,
  onDeleteBoard,
  // Preview-during-drag fetcher (Walter 2026-05-05). See the parent
  // Sidebar's prop docstring.
  onFetchBoardLists,
}) {
  // Which board row the user is currently dragging over. Lets us
  // show a visual highlight so the drop target is obvious.
  const [dropTargetBoardId, setDropTargetBoardId] = useState(null);
  // Board-reorder drag state: which board id is being dragged AS A
  // BOARD (not as a list — that's a different MIME). Where we'd
  // insert if dropped now (before/after target row). Both clear
  // on drag end.
  const [boardDragId, setBoardDragId] = useState(null);
  const [boardDropTarget, setBoardDropTarget] = useState(null);
  // Preview-during-drag state: while dragging a list onto a non-
  // active board, after a short hover delay the target board's
  // MAIN rows expand inline so the user can drop at a specific
  // position. Single value because only one board previews at a time.
  const [previewBoardId, setPreviewBoardId] = useState(null);
  const [previewLists, setPreviewLists] = useState([]);
  const [previewDropIndicator, setPreviewDropIndicator] = useState(null); // { id, pos: 'before' | 'after' }
  const previewHoverTimerRef = useRef(null);
  const previewFetchSeqRef = useRef(0); // guards against stale fetches when hover bounces
  // Walter 2026-06-26: per-board ⋯ menu (rename / colour / delete). One menu at
  // a time, portalled so the sidebar's overflow:hidden can't clip it. The
  // trigger ref points at whichever row's ⋯ is currently open.
  const [menuBoardId, setMenuBoardId] = useState(null);
  const menuTriggerRef = useRef(null);
  const [renamingBoardId, setRenamingBoardId] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const renameCancelRef = useRef(false);
  const startRename = useCallback((b) => {
    renameCancelRef.current = false;
    setMenuBoardId(null);
    setRenameDraft(b.name || '');
    setRenamingBoardId(b.id);
  }, []);
  const commitRename = useCallback((b) => {
    setRenamingBoardId(null);
    if (renameCancelRef.current) { renameCancelRef.current = false; return; }
    const n = renameDraft.trim();
    if (n && n !== b.name) onRenameBoard?.(b.id, n);
  }, [renameDraft, onRenameBoard]);
  const menuBoard = (boards || []).find(b => b.id === menuBoardId) || null;
  const clearPreview = useCallback(() => {
    if (previewHoverTimerRef.current) {
      clearTimeout(previewHoverTimerRef.current);
      previewHoverTimerRef.current = null;
    }
    setPreviewBoardId(null);
    setPreviewLists([]);
    setPreviewDropIndicator(null);
  }, []);
  // Schedule a preview after a short hover delay. Walter wanted
  // "short, customary" — 150ms is the sweet spot between
  // ergonomic-fast and not-flickery if the cursor crosses many rows.
  const schedulePreview = useCallback((targetBoardId) => {
    if (!onFetchBoardLists) return;
    if (previewBoardId === targetBoardId) return;
    if (previewHoverTimerRef.current) clearTimeout(previewHoverTimerRef.current);
    previewHoverTimerRef.current = setTimeout(async () => {
      previewHoverTimerRef.current = null;
      const seq = ++previewFetchSeqRef.current;
      try {
        const fetched = await onFetchBoardLists(targetBoardId);
        if (seq !== previewFetchSeqRef.current) return; // a newer hover superseded
        const main = (Array.isArray(fetched) ? fetched : [])
          .filter(l => l.state !== 'archived')
          .sort((a, b) => {
            const ao = a.order == null ? Number.POSITIVE_INFINITY : a.order;
            const bo = b.order == null ? Number.POSITIVE_INFINITY : b.order;
            if (ao !== bo) return ao - bo;
            return (a.id || '').localeCompare(b.id || '');
          });
        setPreviewBoardId(targetBoardId);
        setPreviewLists(main);
      } catch (err) {
        console.warn('[BoardsSection] preview fetch failed', err);
      }
    }, 150);
  }, [onFetchBoardLists, previewBoardId]);
  // Tear down on unmount so a pending timer can't fire after.
  useEffect(() => () => {
    if (previewHoverTimerRef.current) clearTimeout(previewHoverTimerRef.current);
  }, []);
  // Clear preview when ANY drag ends — covers releases over empty
  // space, ESC cancellation, etc. Without this the preview stays
  // visible until the next hover triggers a new fetch, which feels
  // broken to the user.
  useEffect(() => {
    if (previewBoardId == null) return;
    const onEnd = () => clearPreview();
    window.addEventListener('dragend', onEnd);
    window.addEventListener('drop', onEnd);
    return () => {
      window.removeEventListener('dragend', onEnd);
      window.removeEventListener('drop', onEnd);
    };
  }, [previewBoardId, clearPreview]);

  // Sort by `order` (matches the top-dropdown's reorder behavior),
  // then by `createdAt` for stable ties — newer boards land at the
  // bottom of each order-tier. Walter 2026-05-10: previously tied by
  // id (base62 auto-id), which scattered fresh imports randomly
  // through the unordered tier. createdAt makes "new boards appear
  // at the end" actually true.
  //
  // Walter 2026-06-13: this useMemo MUST run UNCONDITIONALLY — it sits
  // ABOVE the boards-empty early return below. A hook placed after that
  // return changed the hook count whenever `boards` flipped empty↔non-empty
  // on a still-mounted sidebar, which is a React #300 white-screen. It hit
  // on an anonymous→real sign-in while Firestore was blocked (the new
  // account's boards never loaded, so `boards` went to [] on the mounted
  // section). Guard the spread since we now run before the empty check.
  const sorted = useMemo(() => {
    if (!Array.isArray(boards)) return [];
    const arr = [...boards];
    arr.sort((a, b) => {
      const ao = a.order == null ? Number.POSITIVE_INFINITY : a.order;
      const bo = b.order == null ? Number.POSITIVE_INFINITY : b.order;
      if (ao !== bo) return ao - bo;
      const am = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
      const bm = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
      return am - bm;
    });
    return arr;
  }, [boards]);

  const boardHasReminder = (boardId) => {
    if (!Array.isArray(reminders) || reminders.length === 0) return false;
    return reminders.some(r => {
      if (r.status === 'done') return false;
      const rBoardId = r.sourceBoardId || (listIdToBoardId && listIdToBoardId[r.sourceListId]);
      return rBoardId === boardId;
    });
  };

  return (
    <div>
      {/* Walter 2026-06-02: dropped the "BOARDS" header — it sat above
          the list of boards and added no real value (a test user found
          it confusing). The board rows are self-evident, and the
          "Shared with me" section below carries its own label. Small top
          spacer so the first board isn't flush against the checklist. */}
      <div style={{ height: 6 }} />
      {sorted.map(b => {
        const isActive = b.id === currentBoardId;
        const hasReminder = boardHasReminder(b.id);
        return (
          <React.Fragment key={b.id}>
            <a
              className="board-nav-row"
              href={`/board/${b.id}`}
              draggable={!!onReorderBoards}
              // Walter's I.2 (2026-05-05): real anchor so ctrl/cmd-
              // click + right-click "Open in new tab" all work
              // natively. Plain click prevents default + SPA-navigates.
              onClick={(e) => {
                if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return;
                e.preventDefault();
                // Drag-then-click guard: dragend fires before click,
                // but boardDragId may briefly remain in state. Skip
                // SPA nav if a drag was the actual gesture.
                if (boardDragId) return;
                if (!isActive) onNavigateBoard?.(b.id);
              }}
              onDragStart={(e) => {
                if (!onReorderBoards) return;
                setBoardDragId(b.id);
                try {
                  e.dataTransfer.effectAllowed = 'move';
                  // Distinct MIME so the cross-board list-drop path
                  // can sniff types and ignore board-reorder drags.
                  e.dataTransfer.setData('application/x-listicity-board-id', b.id);
                } catch {}
              }}
              onDragEnd={() => {
                setBoardDragId(null);
                setBoardDropTarget(null);
              }}
              onDragOver={(e) => {
                const types = e.dataTransfer.types;
                // Board-reorder hover: highlight insertion line above
                // or below this row depending on cursor y-position.
                if (types.includes('application/x-listicity-board-id')) {
                  if (!boardDragId || boardDragId === b.id) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  const rect = e.currentTarget.getBoundingClientRect();
                  const isAbove = e.clientY < rect.top + rect.height / 2;
                  const pos = isAbove ? 'before' : 'after';
                  if (boardDropTarget?.id !== b.id || boardDropTarget?.pos !== pos) {
                    setBoardDropTarget({ id: b.id, pos });
                  }
                  return;
                }
                // Cross-board list drag (existing behavior):
                if (isActive || !onMoveList) return;
                if (!types.includes('application/x-listicity-list-id')) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (dropTargetBoardId !== b.id) setDropTargetBoardId(b.id);
                // Schedule the inline preview after a short hover delay.
                schedulePreview(b.id);
              }}
              onDragLeave={(e) => {
                if (e.currentTarget.contains(e.relatedTarget)) return;
                if (dropTargetBoardId === b.id) setDropTargetBoardId(null);
                // Cancel a pending preview if the cursor left before
                // the timer fired. (An already-shown preview stays
                // visible — the user may still drop on one of its
                // rows; we only clear it once they hover a different
                // board or finish the drag.)
                if (previewHoverTimerRef.current && previewBoardId !== b.id) {
                  clearTimeout(previewHoverTimerRef.current);
                  previewHoverTimerRef.current = null;
                }
              }}
              onDrop={async (e) => {
                const types = e.dataTransfer.types;
                // Board-reorder drop:
                if (types.includes('application/x-listicity-board-id')) {
                  const sourceId = e.dataTransfer.getData('application/x-listicity-board-id');
                  const pos = boardDropTarget?.pos || 'after';
                  setBoardDragId(null);
                  setBoardDropTarget(null);
                  if (sourceId && sourceId !== b.id && onReorderBoards) {
                    e.preventDefault();
                    e.stopPropagation();
                    onReorderBoards(sourceId, b.id, pos);
                  }
                  return;
                }
                // Cross-board list drop (existing):
                if (isActive || !onMoveList) return;
                const listId = e.dataTransfer.getData('application/x-listicity-list-id');
                setDropTargetBoardId(null);
                clearPreview();
                if (!listId) return;
                e.preventDefault();
                e.stopPropagation();
                // Anchor-area drop: no specific position. Falls back to
                // the original "move and let the natural ordering
                // place it" behavior. Specific-position drops happen
                // on preview rows (see below).
                await onMoveList(listId, b.id);
                onNavigateBoard?.(b.id);
              }}
              style={{
                position: 'relative', // anchor for board-reorder insertion-line indicators
                display: 'flex', alignItems: 'center', gap: 8,
                // Walter 2026-07-02: mirror the new list system on boards too —
                // the active board is marked by a left ACCENT BAR (4px, a touch
                // wider than the list's 3px so the parent reads as heavier), NOT
                // a background wash. Bold + accent text still carry the signal.
                // The drop-target indicator reuses the same bar.
                // Walter 2026-07-03: active board = accent BAR + accent-wash
                // BACKGROUND (both). The board's own COLOUR rides the name's
                // font colour (below), not the bar or background.
                padding: isActive ? '7px 12px 7px 8px' : '6px 12px',
                borderLeft: `${isActive ? 4 : (dropTargetBoardId === b.id ? 3 : 0)}px solid ${(isActive || dropTargetBoardId === b.id) ? 'var(--accent)' : 'transparent'}`,
                background: (isActive || dropTargetBoardId === b.id) ? 'linear-gradient(var(--accent-wash), var(--accent-wash))' : 'transparent',
                color: getListColorTextValue(b.color)
                  || ((isActive || dropTargetBoardId === b.id) ? 'var(--accent)' : 'var(--text)'),
                fontFamily: 'var(--mono)',
                // Walter 2026-05-14: boards bumped to 14px (was 12)
                // so the boards-then-lists hierarchy reads visually
                // in the sidebar — boards as parent containers, lists as
                // children.
                fontSize: 'calc(14px * var(--ui-scale, 1))',
                fontWeight: (isActive || b.color) ? 600 : 500,
                cursor: isActive ? 'default' : 'pointer',
                userSelect: 'none',
                transition: 'background 0.1s, color 0.1s',
                textDecoration: 'none',
                opacity: boardDragId === b.id ? 0.4 : 1,
              }}
              onMouseEnter={e => { if (!isActive && dropTargetBoardId !== b.id) e.currentTarget.style.background = 'linear-gradient(var(--surface2), var(--surface2))'; }}
              onMouseLeave={e => { if (!isActive && dropTargetBoardId !== b.id) e.currentTarget.style.background = 'transparent'; }}
            >
              {/* Board-reorder insertion lines — top edge if pos=before,
                  bottom edge if pos=after. Same visual language as the
                  list-row reorder indicators so the gesture reads
                  consistently across the sidebar. */}
              {boardDropTarget?.id === b.id && boardDropTarget?.pos === 'before' && (
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0,
                  height: 2, background: 'var(--accent)', pointerEvents: 'none',
                }} />
              )}
              {boardDropTarget?.id === b.id && boardDropTarget?.pos === 'after' && (
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  height: 2, background: 'var(--accent)', pointerEvents: 'none',
                }} />
              )}
              {/* Walter 2026-07-03: the per-board colour dot is gone — the
                  board's colour now rides the name's font colour. */}
              {renamingBoardId === b.id ? (
                <input
                  autoFocus
                  value={renameDraft}
                  onChange={e => setRenameDraft(e.target.value)}
                  onClick={e => { e.preventDefault(); e.stopPropagation(); }}
                  onMouseDown={e => e.stopPropagation()}
                  onKeyDown={e => {
                    e.stopPropagation();
                    if (e.key === 'Enter') { e.preventDefault(); commitRename(b); }
                    else if (e.key === 'Escape') { e.preventDefault(); renameCancelRef.current = true; setRenamingBoardId(null); }
                  }}
                  onBlur={() => commitRename(b)}
                  style={{
                    flex: 1, minWidth: 0,
                    background: 'var(--surface)', border: '1px solid var(--accent)',
                    color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: 'inherit',
                    borderRadius: 'var(--radius)', padding: '2px 6px', outline: 'none',
                  }}
                />
              ) : (
                // Walter 2026-07-04: natural width (flex 0 1 auto), so the
                // share/bell markers sit right after the name — matching the
                // list rows — instead of being pushed to the right edge by a
                // flex:1 name. The spacer below keeps the ⋮ at the far right.
                <span style={{
                  flex: '0 1 auto', minWidth: 0,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {b.name}
                </span>
              )}
              <ShareBadge
                editors={b.editors}
                viewers={b.viewers}
                shareToken={b.shareToken}
                onClick={() => onPeekShareBoard?.(b)}
                tone="dim"
              />
              {hasReminder && (
                <BellGlyph title="this board has reminders" />
              )}
              {/* Spacer pushes the ⋮ to the right edge while the name +
                  markers stay left, together. Skipped while renaming so it
                  doesn't fight the flex:1 rename input for width. */}
              {renamingBoardId !== b.id && (
                <span style={{ flex: 1, minWidth: 0 }} aria-hidden="true" />
              )}
              {/* Board ⋯ — rename / colour / delete / sort (Walter 2026-06-26,
                  stopPropagation so it never triggers the row's nav/drag. */}
              {onRenameBoard && renamingBoardId !== b.id && (
                <button
                  type="button"
                  className="board-nav-kebab"
                  title="board options"
                  onMouseDown={e => e.stopPropagation()}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    menuTriggerRef.current = e.currentTarget;
                    setMenuBoardId(id => (id === b.id ? null : b.id));
                  }}
                  style={{
                    flexShrink: 0, background: 'none', border: 'none', padding: '2px',
                    cursor: 'pointer', display: 'inline-flex', alignItems: 'center',
                    color: menuBoardId === b.id ? 'var(--accent)' : 'var(--text-dimmer)',
                    transition: 'color 0.15s, opacity 0.12s',
                    // Walter 2026-07-04: hover-only, matching the list rows (was
                    // always-on, which read as inconsistent). Force visible while
                    // this board's menu is open so it doesn't vanish mid-use.
                    opacity: menuBoardId === b.id ? 1 : undefined,
                  }}
                  onMouseEnter={e => { if (menuBoardId !== b.id) e.currentTarget.style.color = 'var(--text)'; }}
                  onMouseLeave={e => { if (menuBoardId !== b.id) e.currentTarget.style.color = 'var(--text-dimmer)'; }}
                >
                  <svg width="13" height="13" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                    <circle cx="6" cy="2.5" r="1" fill="currentColor" />
                    <circle cx="6" cy="6" r="1" fill="currentColor" />
                    <circle cx="6" cy="9.5" r="1" fill="currentColor" />
                  </svg>
                </button>
              )}
            </a>
            {/* Preview-during-drag rows for non-active boards. Shown
                while a list drag hovers this row long enough for the
                fetch to land. Each row is a drop target — top half =
                before, bottom half = after — and computes a new
                `order` value from neighbor rows so the moved list
                lands exactly where the user dropped. */}
            {!isActive && previewBoardId === b.id && previewLists.length > 0 && (
              <div style={{
                marginLeft: 14,
                borderLeft: '1px solid var(--border)',
                paddingBottom: 4,
                marginBottom: 4,
              }}>
                {previewLists.map((pl, idx) => {
                  const isIndicatorBefore = previewDropIndicator?.id === pl.id && previewDropIndicator?.pos === 'before';
                  const isIndicatorAfter = previewDropIndicator?.id === pl.id && previewDropIndicator?.pos === 'after';
                  const previewLabel = (pl.title?.trim() || firstLine(pl.content) || '(empty)');
                  return (
                    <div
                      key={pl.id}
                      onDragOver={(e) => {
                        if (!e.dataTransfer.types.includes('application/x-listicity-list-id')) return;
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                        const rect = e.currentTarget.getBoundingClientRect();
                        const isAbove = e.clientY < rect.top + rect.height / 2;
                        const pos = isAbove ? 'before' : 'after';
                        if (previewDropIndicator?.id !== pl.id || previewDropIndicator?.pos !== pos) {
                          setPreviewDropIndicator({ id: pl.id, pos });
                        }
                      }}
                      onDragLeave={(e) => {
                        if (e.currentTarget.contains(e.relatedTarget)) return;
                        if (previewDropIndicator?.id === pl.id) setPreviewDropIndicator(null);
                      }}
                      onDrop={async (e) => {
                        if (!onMoveList) return;
                        const listId = e.dataTransfer.getData('application/x-listicity-list-id');
                        if (!listId) return;
                        e.preventDefault();
                        e.stopPropagation();
                        const pos = previewDropIndicator?.pos === 'after' ? 'after' : 'before';
                        // Compute new order from neighbors. Mirrors
                        // the in-section reorder math: midpoint when
                        // both neighbors have orders; +1 / -1 when one
                        // edge; full renumber when ANY neighbor lacks
                        // `order` (legacy data).
                        let insertAt = idx + (pos === 'after' ? 1 : 0);
                        const before = previewLists[insertAt - 1];
                        const after = previewLists[insertAt];
                        const beforeOrder = before?.order;
                        const afterOrder = after?.order;
                        let newOrder;
                        if (beforeOrder != null && afterOrder != null) newOrder = (beforeOrder + afterOrder) / 2;
                        else if (beforeOrder != null) newOrder = beforeOrder + 1;
                        else if (afterOrder != null) newOrder = afterOrder - 1;
                        else newOrder = 0;
                        setDropTargetBoardId(null);
                        clearPreview();
                        await onMoveList(listId, b.id, newOrder);
                        onNavigateBoard?.(b.id);
                      }}
                      style={{
                        position: 'relative',
                        padding: '4px 12px 4px 10px',
                        fontFamily: 'var(--mono)',
                        fontSize: 'calc(11px * var(--ui-scale, 1))',
                        color: 'var(--text-dim)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        userSelect: 'none',
                        cursor: 'default',
                        opacity: 0.85,
                      }}
                    >
                      {isIndicatorBefore && (
                        <div style={{
                          position: 'absolute', top: 0, left: 0, right: 0,
                          height: 2, background: 'var(--accent)', pointerEvents: 'none',
                        }} />
                      )}
                      {isIndicatorAfter && (
                        <div style={{
                          position: 'absolute', bottom: 0, left: 0, right: 0,
                          height: 2, background: 'var(--accent)', pointerEvents: 'none',
                        }} />
                      )}
                      {previewLabel}
                    </div>
                  );
                })}
              </div>
            )}
            {/* Active board: render lists/archive inline, indented
                + with a subtle accent rail on the left so the lists
                read as belonging to this board. Without the rail
                it's easy to mistake the next collapsed board row
                for an archived list of the active board. */}
            {isActive && (
              <div style={{
                marginLeft: 14,
                borderLeft: '1px solid var(--border)',
                paddingBottom: 8,
                marginBottom: 4,
              }}>
                {activeListsContent}
              </div>
            )}
          </React.Fragment>
        );
      })}
      {onNewBoard && (
        <div
          onClick={onNewBoard}
          style={{
            padding: '7px 12px',
            cursor: 'pointer',
            fontFamily: 'var(--mono)',
            fontSize: 'calc(11px * var(--ui-scale, 1))',
            color: 'var(--accent)',
            userSelect: 'none',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          title="create a new board"
        >
          + new board
        </div>
      )}
      {/* Board ⋯ menu — one instance, portalled to escape the sidebar's
          overflow:hidden, positioned at whichever row's ⋯ is open. Colour
          swatches render INLINE (not a fly-out) since the portal would clip a
          horizontal fly-out. Walter 2026-06-26. */}
      <PortalMenu
        open={!!menuBoard}
        triggerRef={menuTriggerRef}
        onClose={() => setMenuBoardId(null)}
        minWidth={170}
      >
        {menuBoard && (
          <>
            <MenuItem icon={<IconPencil />} onClick={() => startRename(menuBoard)}>rename</MenuItem>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 14px 2px', fontFamily: 'var(--mono)', fontSize: 'calc(11px * var(--ui-scale, 1))', color: 'var(--text-dim)' }}>
              <span style={{ display: 'inline-flex', width: 14, justifyContent: 'center', opacity: 0.85 }}><IconColor /></span>
              <span>colour</span>
            </div>
            <ColorSwatches
              current={menuBoard.color}
              onPick={(id) => { onUpdateBoard?.(menuBoard.id, { color: id || null }); setMenuBoardId(null); }}
            />
            {/* Walter 2026-07-04: list-sort moved here from the standalone
                row icon. Only the ACTIVE board can sort (the reorder is only
                visible on the board you're looking at), and only with ≥2
                lists. Rendered INLINE like the colour swatches — a fly-out
                would clip against the portal edge. */}
            {menuBoard.id === currentBoardId && canSortActive && onSortLists && (
              <>
                <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 14px 2px', fontFamily: 'var(--mono)', fontSize: 'calc(11px * var(--ui-scale, 1))', color: 'var(--text-dim)' }}>
                  <span style={{ display: 'inline-flex', width: 14, justifyContent: 'center', opacity: 0.85 }}><IconSort /></span>
                  <span>sort lists</span>
                </div>
                <MenuItem indent onClick={() => { onSortLists('canvas'); setMenuBoardId(null); }}>canvas arrangement</MenuItem>
                <MenuItem indent onClick={() => { onSortLists('alpha'); setMenuBoardId(null); }}>alphabetical</MenuItem>
              </>
            )}
            {(boards || []).length > 1 && (
              <>
                <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />
                <MenuItem
                  icon={<IconTrash />} color="var(--danger)"
                  onClick={() => {
                    if (window.confirm(`Move board "${menuBoard.name}" and all its lists to trash?\n\nYou can restore it within 30 days from the trash menu (account popover → trash).`)) {
                      onDeleteBoard?.(menuBoard.id);
                    }
                    setMenuBoardId(null);
                  }}
                >delete</MenuItem>
              </>
            )}
          </>
        )}
      </PortalMenu>
    </div>
  );
}

/**
 * SharedWithMeSection — sidebar block listing boards AND lists shared
 * with the current user. Each entry: title, owner-cluster glyph, role
 * badge (ed/rd). Click navigates to the right URL: /board/:id for
 * shared boards, /list/:id for shared standalone lists.
 *
 * Hidden in compact (mobile-strip) sidebar mode — there's no room
 * there and the user can navigate via the board dropdown anyway.
 */
function SharedWithMeSection({ items, placements = {}, onNavigateBoard, onNavigateList, currentBoardId }) {
  const [open, setOpen] = useState(true);
  const boards = items.filter(it => it.kind === 'board');
  // Walter 2026-05-11: a shared list with a placement record has been
  // adopted into one of the user's own boards — render it there
  // instead. Hiding it here keeps "shared with me" as the unplaced /
  // orphan surface, exactly what the design calls for ("becomes
  // quieter: shows shared BOARDS + any shared lists with no
  // placement set"). The placement map is keyed by listId, same as
  // the sharedWithMe pointer's refId for list-kind entries.
  const lists = items.filter(it => it.kind === 'list' && !placements[it.refId]);
  if (boards.length === 0 && lists.length === 0) return null;
  return (
    <div style={{ marginTop: '14px', paddingTop: '8px', borderTop: '1px solid var(--border)' }}>
      <SectionHeader
        label="shared with me"
        count={boards.length + lists.length}
        open={open}
        onToggle={() => setOpen(v => !v)}
        subtle
      />
      {open && boards.map(b => {
        const isCurrent = b.refId === currentBoardId;
        const title = b.cachedTitle || '(untitled)';
        return (
          <SharedRow
            key={b.id}
            title={title}
            ownerName={b.cachedOwnerName}
            role={b.role}
            isCurrent={isCurrent}
            onClick={() => onNavigateBoard?.(b.refId)}
            href={`/board/${b.refId}`}
            kind="board"
          />
        );
      })}
      {open && lists.map(l => {
        // Standalone shared list — no "currently viewing" highlighting
        // for the active /list/:id case yet (would require passing
        // currentListId down). Acceptable for v1.
        const title = l.cachedTitle || '(untitled list)';
        return (
          <SharedRow
            key={l.id}
            title={title}
            ownerName={l.cachedOwnerName}
            role={l.role}
            isCurrent={false}
            onClick={() => onNavigateList?.(l.refId)}
            href={`/list/${l.refId}`}
            kind="list"
          />
        );
      })}
    </div>
  );
}

function SharedRow({ title, ownerName, role, isCurrent, onClick, kind, href }) {
  const trimmedOwner = (ownerName || '').trim();
  return (
    <a
      href={href}
      onClick={(e) => {
        if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return;
        e.preventDefault();
        onClick?.();
      }}
      style={{
        // Walter 2026-05-11: switched to flex-start alignment so the
        // owner sub-line (when present) reads as a stacked caption
        // under the title rather than wrapping awkwardly.
        display: 'flex', alignItems: 'flex-start', gap: '8px',
        padding: '6px 10px', cursor: 'pointer',
        fontFamily: 'var(--mono)',
        fontSize: 'calc(11px * var(--ui-scale, 1))',
        background: isCurrent ? 'var(--accent-wash)' : 'transparent',
        borderLeft: isCurrent ? '2px solid var(--accent)' : '2px solid transparent',
        color: isCurrent ? 'var(--accent)' : 'var(--text)',
        transition: 'background 0.12s, color 0.12s',
        textDecoration: 'none',
      }}
      onMouseEnter={e => { if (!isCurrent) e.currentTarget.style.background = 'var(--surface2)'; }}
      onMouseLeave={e => { if (!isCurrent) e.currentTarget.style.background = 'transparent'; }}
    >
      {kind === 'board' ? (
        <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true" style={{ flexShrink: 0, opacity: 0.7, marginTop: 2 }}>
          <rect x="0.5" y="0.5" width="4" height="4" rx="0.6" stroke="currentColor" strokeWidth="1"/>
          <rect x="6"   y="0.5" width="4" height="4" rx="0.6" stroke="currentColor" strokeWidth="1"/>
          <rect x="0.5" y="6"   width="4" height="4" rx="0.6" stroke="currentColor" strokeWidth="1"/>
          <rect x="6"   y="6"   width="4" height="4" rx="0.6" stroke="currentColor" strokeWidth="1"/>
        </svg>
      ) : (
        <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true" style={{ flexShrink: 0, opacity: 0.7, marginTop: 2 }}>
          <rect x="1" y="2" width="9" height="2" rx="0.4" stroke="currentColor" strokeWidth="1"/>
          <rect x="1" y="5" width="9" height="2" rx="0.4" stroke="currentColor" strokeWidth="1"/>
          <rect x="1" y="8" width="9" height="2" rx="0.4" stroke="currentColor" strokeWidth="1"/>
        </svg>
      )}
      <span style={{
        flex: 1, minWidth: 0,
        display: 'flex', flexDirection: 'column', gap: 1,
      }}>
        <span style={{
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {title}
        </span>
        {/* Owner sub-line (Walter 2026-05-11). Cached on the pointer
            doc by the cloud-function accept path; absent for older
            pointers or for client-side lazy writes (useResolvedBoard
            / useResolvedList) — show nothing in those cases rather
            than a generic placeholder. */}
        {trimmedOwner && (
          <span style={{
            fontSize: 'calc(9px * var(--ui-scale, 1))',
            color: 'var(--text-dimmer)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            shared by {trimmedOwner}
          </span>
        )}
      </span>
      <span title={role === 'editor' ? 'can edit' : 'can view'} style={{
        flexShrink: 0,
        fontSize: 'calc(8px * var(--ui-scale, 1))',
        color: 'var(--text-dimmer)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        padding: '0 4px',
        letterSpacing: '0.4px',
        textTransform: 'uppercase',
        marginTop: 2,
      }}>
        {role === 'editor' ? 'ed' : 'rd'}
      </span>
    </a>
  );
}

// Small monochrome bell glyph used for both the board-row "this
// board has reminders" marker and the list-row "designated reminder
// list" marker. Uses currentColor so callers control the tone via
// CSS color (text-dimmer in the sidebar). The earlier emoji 🔔 was
// always yellow — emoji glyphs render in their native colors and
// CSS color doesn't affect them — so making the markers grey
// required swapping to an SVG that respects currentColor.
function BellGlyph({ title }) {
  return (
    <svg
      width="11"
      height="11"
      viewBox="0 0 14 14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{
        flexShrink: 0,
        color: 'var(--text-dimmer)',
      }}
    >
      {title && <title>{title}</title>}
      {/* Bell body */}
      <path d="M3 9.5c0-3 1.7-5 4-5s4 2 4 5l1 1H2l1-1z" />
      {/* Bell top */}
      <path d="M7 4.5V3" />
      {/* Clapper */}
      <path d="M5.6 11.5c0 .8.6 1.4 1.4 1.4s1.4-.6 1.4-1.4" />
    </svg>
  );
}

function Pill({ children }) {
  return (
    <span style={{
      flexShrink: 0,
      fontFamily: 'var(--mono)',
      fontSize: 'calc(9px * var(--ui-scale, 1))',
      color: 'var(--text-dimmer)',
      background: 'var(--bg)',
      border: '1px solid var(--border)',
      padding: '1px 5px', borderRadius: '8px',
      textTransform: 'lowercase',
    }}>
      {children}
    </span>
  );
}

// Two-way (up/down) move glyph for sidebar rows. Sidebar reorder is
// vertical-only, so we drop the left/right axis from the tile version of
// this icon. Positioned to the left of the row title; hidden in compact
// (mobile strip) mode. The row itself is what's `draggable`; the grip is
// purely a visual cue.
function RowDragGrip() {
  return (
    <svg
      width="10"
      height="14"
      viewBox="0 0 10 14"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        flexShrink: 0,
        color: 'var(--text-dim)',
        cursor: 'grab',
      }}
    >
      {/* Vertical axis */}
      <line x1="5" y1="3" x2="5" y2="11" />
      {/* Up + down chevrons */}
      <path d="M3 4 L5 2 L7 4" />
      <path d="M3 10 L5 12 L7 10" />
    </svg>
  );
}

// CompactBoardsMini — slim boards list rendered in the mobile 80px
// peek strip. Walter's II.5 + D (round 5): mirror the desktop
// structure — active board first, its lists nested directly below
// it, then other boards. So switching boards requires one tap on
// the appropriate name and never a scroll past unrelated lists.
//
// Differences from the full BoardsSection:
//   - 80px-wide cells with truncated names, no badges or markers
//   - Active board styled with accent text + accent left border
//   - No "+ new board" row (use peek-expanded for that)
function CompactBoardsMini({ boards, currentBoardId, onNavigateBoard, activeListsContent }) {
  if (!Array.isArray(boards) || boards.length === 0) return null;
  // Same tiebreaker change as the main BoardsSection above —
  // createdAt asc instead of id, so fresh imports land at the bottom
  // of their order-tier instead of scattering alphabetically by id.
  const sorted = [...boards].sort((a, b) => {
    const ao = a.order == null ? Number.POSITIVE_INFINITY : a.order;
    const bo = b.order == null ? Number.POSITIVE_INFINITY : b.order;
    if (ao !== bo) return ao - bo;
    const am = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
    const bm = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
    return am - bm;
  });
  return (
    <div>
      <div style={{
        padding: '6px 8px 2px',
        fontFamily: 'var(--mono)',
        fontSize: 'calc(8px * var(--ui-scale, 1))',
        fontWeight: 600,
        color: 'var(--text-dimmer)',
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
      }}>
        boards
      </div>
      {sorted.map(b => {
        const isActive = b.id === currentBoardId;
        return (
          <React.Fragment key={b.id}>
            <a
              href={`/board/${b.id}`}
              onClick={(e) => {
                if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return;
                e.preventDefault();
                if (!isActive) onNavigateBoard?.(b.id);
              }}
              style={{
                display: 'block',
                padding: '4px 8px',
                // Walter 2026-07-03: active = accent bar + accent-wash bg;
                // board colour rides the name's font colour.
                borderLeft: isActive ? '3px solid var(--accent)' : '3px solid transparent',
                background: isActive ? 'linear-gradient(var(--accent-wash), var(--accent-wash))' : 'transparent',
                color: getListColorTextValue(b.color) || (isActive ? 'var(--accent)' : 'var(--text)'),
                fontFamily: 'var(--mono)',
                // Walter 2026-05-14: compact-strip boards bumped from
                // 10px to 13px so the boards-then-lists hierarchy
                // reads on mobile too (lists in compact mode are
                // 11px). Earlier 10px made boards SMALLER than the
                // lists below them — inverted hierarchy. 13px fits
                // an abbreviated board name in the 80-px peek strip
                // without truncating much harder than before.
                fontSize: 'calc(13px * var(--ui-scale, 1))',
                fontWeight: (isActive || b.color) ? 600 : 500,
                cursor: isActive ? 'default' : 'pointer',
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                transition: 'background 0.1s, color 0.1s',
              }}
              onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'linear-gradient(var(--surface2), var(--surface2))'; }}
              onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
              title={b.name}
            >
              {b.name}
            </a>
            {/* Active board's lists/archive nest directly under its
                row. Walter 2026-05-09: added marginLeft + a subtle
                left rail so the lists read as belonging to the
                highlighted board, not as siblings. Without the
                indent, focused lists got their own 3px accent bar
                at the same x-position as the active-board bar,
                making them look like another board (Walter flagged
                this on iPhone). The rail uses --border so it's a
                quiet structural cue, not a brand signal. */}
            {isActive && (
              <div style={{
                marginLeft: 10,
                borderLeft: '1px solid var(--border)',
                paddingBottom: 6,
                borderBottom: '1px solid var(--border)',
                marginBottom: 4,
              }}>
                {activeListsContent}
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// Pinned-bottom row that opens /trash. Sits below the scrollable
// list/archive section, separated by a top border so it reads as a
// nav footer rather than "another list".
//
// When count is 0: rendered dim, no badge, no visual noise (per
// Walter's pushback — don't promote trash visually when empty).
// When count > 0: accent count badge on the right edge.
function TrashFooter({ onClick, count = 0 }) {
  const [hover, setHover] = useState(false);
  const hasItems = count > 0;
  return (
    <a
      href="/trash"
      onClick={(e) => {
        if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return;
        e.preventDefault();
        onClick?.();
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        // Walter 2026-05-10: borderTop + outer padding moved up to the
        // wrapping <footer> in the sidebar render (which now also holds
        // the marketing-page links inline on the right). TrashFooter
        // keeps its inner padding for the click target / hover surface.
        flexShrink: 0,
        // Walter 2026-06-11: tightened the icon↔"trash" gap (was 8px) so
        // the glyph and word read as one unit rather than drifting apart.
        display: 'flex', alignItems: 'center', gap: '5px',
        // Walter 2026-05-25 (Bug #9): bigger tap zone on mobile.
        padding: '8px 10px',
        margin: '-8px -10px',
        borderRadius: 'var(--radius)',
        cursor: 'pointer',
        userSelect: 'none',
        background: hover ? 'var(--surface2)' : 'transparent',
        transition: 'background 0.1s',
        fontFamily: 'var(--mono)',
        fontSize: 'calc(11px * var(--ui-scale, 1))',
        // Walter 2026-05-05: trash was too easy to miss. Bumped both
        // shades up one notch and thickened the icon stroke. Empty
        // still reads as quieter than non-empty so it doesn't shout.
        color: hover ? 'var(--text)' : (hasItems ? 'var(--text)' : 'var(--text-dim)'),
        textDecoration: 'none',
      }}
      title={hasItems
        ? `open trash (${count} item${count === 1 ? '' : 's'}, 30-day recovery)`
        : 'open trash (empty)'}
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
        <path d="M3 4h8M5 4V2.5h4V4M4 4v8h6V4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {/* Walter 2026-06-16: was flex:1, which (now that trash sits on its
          OWN line) shoved the count badge to the far right edge. Drop it so
          the count sits right next to "trash". */}
      <span style={{ whiteSpace: 'nowrap' }}>
        trash
      </span>
      {hasItems && (
        <span style={{
          flexShrink: 0,
          fontSize: 'calc(9px * var(--ui-scale, 1))',
          color: 'var(--accent)',
          border: '1px solid var(--accent-dim)',
          borderRadius: 'var(--radius)',
          padding: '0 5px',
          fontWeight: 600,
        }}>
          {count}
        </span>
      )}
    </a>
  );
}
